import { Component, inject, OnInit, signal } from '@angular/core';
import { CommonModule, CurrencyPipe, DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { AffiliateService } from '../../core/services/affiliate.service';

@Component({
  selector: 'app-affiliates-dashboard',
  standalone: true,
  imports: [CommonModule, CurrencyPipe, DatePipe, FormsModule],
  template: `
    <main class="min-h-screen bg-stone-950 text-stone-100 pb-20">
      <!-- Top Navigation Banner -->
      <header class="bg-stone-900/90 backdrop-blur border-b border-stone-800 sticky top-0 z-40">
        <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div class="flex items-center gap-3">
            <span class="p-2 rounded-lg bg-amber-500/10 text-amber-400 border border-amber-500/20">
              <span class="material-symbols-outlined text-2xl">handshake</span>
            </span>
            <div>
              <h1 class="text-xl font-serif font-bold text-white flex items-center gap-2">
                Partner Dashboard
                <span class="text-xs font-mono font-normal bg-stone-800 text-amber-300 border border-stone-700 px-2 py-0.5 rounded-md">
                  via={{ stats()?.user?.slug }}
                </span>
              </h1>
              <p class="text-xs text-stone-400">Regency House Cleaning Affiliate Portal</p>
            </div>
          </div>

          <div class="flex items-center gap-3">
            <button
              (click)="refreshData()"
              [disabled]="affiliateService.isLoading()"
              class="p-2.5 rounded-lg bg-stone-800 hover:bg-stone-700 text-stone-300 transition-colors border border-stone-700 text-xs flex items-center gap-1.5"
              title="Refresh Data"
            >
              <span class="material-symbols-outlined text-base" [class.animate-spin]="affiliateService.isLoading()">sync</span>
              <span>Refresh</span>
            </button>
            <button
              (click)="affiliateService.logout()"
              class="px-4 py-2 rounded-lg bg-stone-800 hover:bg-red-950/60 hover:text-red-300 text-stone-300 transition-all border border-stone-700 text-xs font-semibold uppercase tracking-wider flex items-center gap-2"
            >
              <span>Sign Out</span>
              <span class="material-symbols-outlined text-base">logout</span>
            </button>
          </div>
        </div>
      </header>

      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 space-y-8">
        <!-- Status Banners -->
        @if (onboardingSuccess()) {
          <div class="p-4 rounded-xl bg-emerald-950/60 border border-emerald-700/60 text-emerald-200 text-xs flex items-center justify-between gap-3 shadow-lg animate-fade-in">
            <div class="flex items-center gap-2.5">
              <span class="material-symbols-outlined text-emerald-400 text-xl">check_circle</span>
              <span class="font-medium">{{ onboardingSuccess() }}</span>
            </div>
            <button (click)="onboardingSuccess.set(null)" class="text-emerald-400 hover:text-emerald-200 text-base flex items-center">
              <span class="material-symbols-outlined">close</span>
            </button>
          </div>
        }

        @if (onboardingWarning()) {
          <div class="p-4 rounded-xl bg-amber-950/60 border border-amber-700/60 text-amber-200 text-xs flex items-center justify-between gap-3 shadow-lg animate-fade-in">
            <div class="flex items-center gap-2.5">
              <span class="material-symbols-outlined text-amber-400 text-xl">warning</span>
              <span class="font-medium">{{ onboardingWarning() }}</span>
            </div>
            <button (click)="onboardingWarning.set(null)" class="text-amber-400 hover:text-amber-200 text-base flex items-center">
              <span class="material-symbols-outlined">close</span>
            </button>
          </div>
        }

        @if (affiliateService.isLoading() && !stats()) {
          <div class="flex flex-col items-center justify-center py-20 text-stone-400">
            <span class="material-symbols-outlined text-4xl animate-spin text-amber-400 mb-3">sync</span>
            <p class="text-sm font-medium">Loading partner dashboard metrics...</p>
          </div>
        } @else if (stats()) {
          <!-- KPI Stats Header Grid -->
          <section class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            <!-- Total Clicks -->
            <div class="bg-stone-900 border border-stone-800 rounded-xl p-6 shadow-lg relative overflow-hidden">
              <div class="flex items-center justify-between">
                <span class="text-xs font-semibold uppercase tracking-wider text-stone-400">Total Referral Clicks</span>
                <span class="material-symbols-outlined text-amber-400/80 text-2xl">ads_click</span>
              </div>
              <p class="mt-4 text-3xl font-bold text-white font-mono">{{ stats()?.totalClicks || 0 }}</p>
              <p class="mt-1 text-xs text-stone-500">First-party 60-day tracked sessions</p>
            </div>

            <!-- Referred Cleanings -->
            <div class="bg-stone-900 border border-stone-800 rounded-xl p-6 shadow-lg relative overflow-hidden">
              <div class="flex items-center justify-between">
                <span class="text-xs font-semibold uppercase tracking-wider text-stone-400">Referred Cleanings</span>
                <span class="material-symbols-outlined text-emerald-400/80 text-2xl">cleaning_services</span>
              </div>
              <p class="mt-4 text-3xl font-bold text-white font-mono">{{ stats()?.totalReferredCleanings || 0 }}</p>
              <p class="mt-1 text-xs text-stone-500">Confirmed booking conversions</p>
            </div>

            <!-- Unpaid Earnings (Net 30 Pending) -->
            <div class="bg-stone-900 border border-amber-900/40 rounded-xl p-6 shadow-lg relative overflow-hidden bg-gradient-to-br from-stone-900 to-amber-950/20">
              <div class="flex items-center justify-between">
                <span class="text-xs font-semibold uppercase tracking-wider text-amber-300">Net-30 Pending Unpaid</span>
                <span class="material-symbols-outlined text-amber-400 text-2xl">hourglass_top</span>
              </div>
              <p class="mt-4 text-3xl font-bold text-amber-400 font-mono">
                {{ ((stats()?.unpaidEarningsPendingCents || 0) / 100) | currency:'USD':'symbol':'1.2-2' }}
              </p>
              <p class="mt-1 text-xs text-amber-200/60">30-day payout hold protection window</p>
            </div>

            <!-- Lifetime Paid -->
            <div class="bg-stone-900 border border-emerald-900/40 rounded-xl p-6 shadow-lg relative overflow-hidden bg-gradient-to-br from-stone-900 to-emerald-950/20">
              <div class="flex items-center justify-between">
                <span class="text-xs font-semibold uppercase tracking-wider text-emerald-300">Lifetime Paid Out</span>
                <span class="material-symbols-outlined text-emerald-400 text-2xl">account_balance_wallet</span>
              </div>
              <p class="mt-4 text-3xl font-bold text-emerald-400 font-mono">
                {{ ((stats()?.lifetimePaidCents || 0) / 100) | currency:'USD':'symbol':'1.2-2' }}
              </p>
              <p class="mt-1 text-xs text-emerald-200/60">
                Via {{ stats()?.user?.payoutMethod === 'paypal' ? 'PayPal Payouts API' : 'Stripe Payouts' }}
              </p>
            </div>
          </section>

          <!-- Referral Center & Payout Setup Row -->
          <section class="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <!-- Link Center (2 cols) -->
            <div class="lg:col-span-2 bg-stone-900 border border-stone-800 rounded-xl p-6 shadow-xl space-y-4">
              <div class="flex items-center justify-between">
                <div class="flex items-center gap-2">
                  <span class="material-symbols-outlined text-amber-400 text-xl">link</span>
                  <h2 class="text-lg font-serif font-bold text-white">Your Referral Center</h2>
                </div>
                <span class="px-2.5 py-1 rounded-full bg-amber-500/10 text-amber-400 border border-amber-500/30 text-xs font-bold">
                  $150 Flat Rate Per Booking
                </span>
              </div>
              <p class="text-xs text-stone-400">Share your unique partner link. Anyone booking a cleaning service through your link earns you a flat $150 commission on each confirmed cleaning booking.</p>

              <div class="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 pt-2">
                <div class="flex-1 relative">
                  <input
                    type="text"
                    readonly
                    [value]="stats()?.referralLink"
                    class="w-full bg-stone-950 border border-stone-700 rounded-lg px-4 py-3 text-amber-300 text-sm font-mono focus:outline-none select-all"
                  />
                </div>
                <button
                  (click)="copyReferralLink()"
                  class="px-5 py-3 rounded-lg bg-amber-500 hover:bg-amber-400 text-stone-950 font-bold uppercase tracking-wider text-xs transition-all flex items-center justify-center gap-2 shrink-0"
                >
                  <span class="material-symbols-outlined text-base">
                    {{ copied() ? 'check_circle' : 'content_copy' }}
                  </span>
                  <span>{{ copied() ? 'Copied!' : 'Copy Partner Link' }}</span>
                </button>
              </div>

              <!-- Promo Code snippet tip -->
              <div class="p-3.5 rounded-lg bg-stone-950/60 border border-stone-800 text-xs text-stone-400 flex items-center justify-between gap-4">
                <span class="flex items-center gap-2">
                  <span class="material-symbols-outlined text-stone-400 text-base">code</span>
                  <span>Direct URL Parameter: <code class="text-amber-400 font-mono">?via={{ stats()?.user?.slug }}</code></span>
                </span>
                <span class="text-stone-500 text-[11px]">Auto-sets 60-day cookie</span>
              </div>
            </div>

            <!-- Payout Settings Card (Stripe & PayPal API) (1 col) -->
            <div class="bg-stone-900 border border-stone-800 rounded-xl p-6 shadow-xl flex flex-col justify-between space-y-4">
              <div>
                <div class="flex items-center justify-between mb-3">
                  <div class="flex items-center gap-2">
                    <span class="material-symbols-outlined text-indigo-400 text-xl">payments</span>
                    <h2 class="text-lg font-serif font-bold text-white">Payout Method</h2>
                  </div>
                  <span
                    class="px-2 py-0.5 rounded text-[11px] font-semibold uppercase tracking-wide border"
                    [class]="stats()?.user?.payoutMethod === 'paypal' ? 'bg-sky-500/10 text-sky-400 border-sky-500/30' : 'bg-indigo-500/10 text-indigo-400 border-indigo-500/30'"
                  >
                    Active: {{ stats()?.user?.payoutMethod === 'paypal' ? 'PayPal API' : 'Stripe' }}
                  </span>
                </div>
                <p class="text-xs text-stone-400 mb-4">Choose your preferred automated Net-30 payout destination.</p>

                <!-- Method Switcher Tabs -->
                <div class="grid grid-cols-2 gap-2 p-1 bg-stone-950 rounded-lg border border-stone-800 mb-4">
                  <button
                    (click)="setTab('stripe')"
                    [class]="selectedPayoutTab() === 'stripe' ? 'bg-stone-800 text-white font-bold border border-stone-700' : 'text-stone-400 hover:text-stone-200'"
                    class="py-2 px-3 rounded-md text-xs transition-all flex items-center justify-center gap-1.5"
                  >
                    <span class="material-symbols-outlined text-base text-indigo-400">credit_card</span>
                    <span>Stripe Payouts</span>
                  </button>
                  <button
                    (click)="setTab('paypal')"
                    [class]="selectedPayoutTab() === 'paypal' ? 'bg-stone-800 text-white font-bold border border-stone-700' : 'text-stone-400 hover:text-stone-200'"
                    class="py-2 px-3 rounded-md text-xs transition-all flex items-center justify-center gap-1.5"
                  >
                    <span class="material-symbols-outlined text-base text-sky-400">account_balance_wallet</span>
                    <span>PayPal API</span>
                  </button>
                </div>

                <!-- Stripe Section -->
                @if (selectedPayoutTab() === 'stripe') {
                  @if (stats()?.user?.stripeConnectOnboarded) {
                    <div class="p-3.5 rounded-lg bg-emerald-950/40 border border-emerald-800/60 text-emerald-300 text-xs flex items-center gap-3">
                      <span class="material-symbols-outlined text-emerald-400 text-xl">verified</span>
                      <div>
                        <p class="font-bold text-emerald-200">Stripe Account Active</p>
                        <p class="text-emerald-400/80 text-[11px]">Automatic Net-30 payouts enabled.</p>
                      </div>
                    </div>
                  } @else {
                    <div class="p-3.5 rounded-lg bg-amber-950/40 border border-amber-800/60 text-amber-300 text-xs flex items-start gap-3">
                      <span class="material-symbols-outlined text-amber-400 text-xl shrink-0">warning</span>
                      <div>
                        <p class="font-bold text-amber-200">Stripe Account Unlinked</p>
                        <p class="text-amber-300/80 text-[11px]">Connect Stripe account to receive automated payouts.</p>
                      </div>
                    </div>
                  }
                }

                <!-- PayPal Section -->
                @if (selectedPayoutTab() === 'paypal') {
                  <div class="space-y-3">
                    @if (stats()?.user?.paypalEmail && stats()?.user?.payoutMethod === 'paypal') {
                      <div class="p-3.5 rounded-lg bg-sky-950/40 border border-sky-800/60 text-sky-300 text-xs flex items-center gap-3">
                        <span class="material-symbols-outlined text-sky-400 text-xl">verified</span>
                        <div>
                          <p class="font-bold text-sky-200">PayPal Active: {{ stats()?.user?.paypalEmail }}</p>
                          <p class="text-sky-400/80 text-[11px]">Net-30 payouts sent via PayPal Payouts API.</p>
                        </div>
                      </div>
                    }

                    <div>
                      <label class="block text-[11px] font-semibold text-stone-300 uppercase tracking-wider mb-1">
                        PayPal Account Email
                      </label>
                      <input
                        type="email"
                        name="paypalEmail"
                        [(ngModel)]="paypalEmailInput"
                        (input)="paypalEmailInput = $any($event.target).value"
                        placeholder="yourname@paypal.com"
                        class="w-full bg-stone-950 border border-stone-700 rounded-lg px-3.5 py-2.5 text-xs text-stone-100 placeholder-stone-600 focus:outline-none focus:border-sky-500"
                      />
                    </div>
                  </div>
                }
              </div>

              <!-- Action Buttons -->
              <div>
                @if (selectedPayoutTab() === 'stripe') {
                  @if (stats()?.user?.stripeConnectOnboarded) {
                    <button
                      (click)="onOpenStripePortal()"
                      [disabled]="isPortalLoading()"
                      class="w-full py-3 px-4 rounded-lg bg-stone-800 hover:bg-stone-700 text-stone-200 border border-stone-700 font-bold uppercase tracking-wider text-xs transition-all flex items-center justify-center gap-2 shadow-lg disabled:opacity-50"
                    >
                      @if (isPortalLoading()) {
                        <span class="material-symbols-outlined animate-spin text-base">sync</span>
                        <span>Opening Stripe Portal...</span>
                      } @else {
                        <span class="material-symbols-outlined text-base">open_in_new</span>
                        <span>Manage Stripe Account</span>
                      }
                    </button>
                  } @else {
                    <button
                      (click)="onSetupStripeConnect()"
                      [disabled]="isConnectLoading()"
                      class="w-full py-3 px-4 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white font-bold uppercase tracking-wider text-xs transition-all flex items-center justify-center gap-2 shadow-lg disabled:opacity-50"
                    >
                      @if (isConnectLoading()) {
                        <span class="material-symbols-outlined animate-spin text-base">sync</span>
                        <span>Opening Stripe Onboarding...</span>
                      } @else {
                        <span class="material-symbols-outlined text-base">credit_card</span>
                        <span>Connect Stripe Account</span>
                      }
                    </button>
                  }
                } @else if (selectedPayoutTab() === 'paypal') {
                  <button
                    (click)="onSavePayPalSettings()"
                    [disabled]="isSavingPaypal()"
                    class="w-full py-3 px-4 rounded-lg bg-sky-600 hover:bg-sky-500 text-white font-bold uppercase tracking-wider text-xs transition-all flex items-center justify-center gap-2 shadow-lg disabled:opacity-50"
                  >
                    @if (isSavingPaypal()) {
                      <span class="material-symbols-outlined animate-spin text-base">sync</span>
                      <span>Saving PayPal API Settings...</span>
                    } @else {
                      <span class="material-symbols-outlined text-base">save</span>
                      <span>Connect PayPal API Payouts</span>
                    }
                  </button>
                }
              </div>
            </div>
          </section>

          <!-- Conversion Ledger Section -->
          <section class="bg-stone-900 border border-stone-800 rounded-xl p-6 shadow-xl space-y-4">
            <div class="flex items-center justify-between">
              <div>
                <h2 class="text-lg font-serif font-bold text-white flex items-center gap-2">
                  <span class="material-symbols-outlined text-amber-400">table_view</span>
                  Conversion Ledger
                </h2>
                <p class="text-xs text-stone-400">Detailed list of cleanings booked through your referral link and commission release dates.</p>
              </div>
              <span class="text-xs text-stone-400 font-mono">{{ stats()?.commissions?.length || 0 }} total entries</span>
            </div>

            @if (!stats()?.commissions || stats()?.commissions?.length === 0) {
              <div class="text-center py-12 bg-stone-950/50 rounded-lg border border-stone-800">
                <span class="material-symbols-outlined text-stone-600 text-4xl mb-2">receipt_long</span>
                <p class="text-stone-400 text-sm font-medium">No conversions recorded yet.</p>
                <p class="text-stone-500 text-xs mt-1">Share your referral link to earn your first commission!</p>
              </div>
            } @else {
              <div class="overflow-x-auto">
                <table class="w-full text-left text-xs text-stone-300">
                  <thead class="bg-stone-950 text-stone-400 uppercase text-[10px] tracking-wider border-b border-stone-800">
                    <tr>
                      <th class="py-3 px-4">Date</th>
                      <th class="py-3 px-4">Payment Ref</th>
                      <th class="py-3 px-4">Order Total</th>
                      <th class="py-3 px-4">Commission ($150 Flat)</th>
                      <th class="py-3 px-4">Status</th>
                      <th class="py-3 px-4">Net-30 Release Date</th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-stone-800 font-mono">
                    @for (comm of stats()?.commissions; track comm.id) {
                      <tr class="hover:bg-stone-800/40 transition-colors">
                        <td class="py-3.5 px-4 text-stone-300 font-sans">
                          {{ comm.createdAt | date:'mediumDate' }}
                        </td>
                        <td class="py-3.5 px-4 text-stone-400">
                          {{ comm.paymentIntentId }}
                        </td>
                        <td class="py-3.5 px-4 font-bold text-stone-200">
                          {{ (comm.orderAmountCents / 100) | currency:'USD':'symbol':'1.2-2' }}
                        </td>
                        <td class="py-3.5 px-4 font-bold text-amber-400">
                          {{ (comm.commissionAmountCents / 100) | currency:'USD':'symbol':'1.2-2' }}
                        </td>
                        <td class="py-3.5 px-4 font-sans">
                          @if (comm.status === 'pending') {
                            <span class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-500/10 text-amber-400 border border-amber-500/30 text-[11px] font-semibold">
                              <span class="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse"></span>
                              Net-30 Pending
                            </span>
                          } @else if (comm.status === 'approved') {
                            <span class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-indigo-500/10 text-indigo-300 border border-indigo-500/30 text-[11px] font-semibold">
                              Approved (Payout Due)
                            </span>
                          } @else if (comm.status === 'paid') {
                            <span class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/30 text-[11px] font-semibold">
                              Paid Out
                            </span>
                          } @else {
                            <span class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-red-500/10 text-red-400 border border-red-500/30 text-[11px] font-semibold" [title]="comm.cancelReason || 'Cancelled'">
                              Cancelled
                            </span>
                          }
                        </td>
                        <td class="py-3.5 px-4 text-stone-400 font-sans">
                          {{ comm.payoutDueDate | date:'mediumDate' }}
                        </td>
                      </tr>
                    }
                  </tbody>
                </table>
              </div>
            }
          </section>

          <!-- Historical Payout Log -->
          <section class="bg-stone-900 border border-stone-800 rounded-xl p-6 shadow-xl space-y-4">
            <div class="flex items-center justify-between">
              <div>
                <h2 class="text-lg font-serif font-bold text-white flex items-center gap-2">
                  <span class="material-symbols-outlined text-emerald-400">history</span>
                  Payout History
                </h2>
                <p class="text-xs text-stone-400">Log of completed automated Stripe transfers & PayPal payouts.</p>
              </div>
              <span class="text-xs text-stone-400 font-mono">{{ stats()?.payouts?.length || 0 }} payouts</span>
            </div>

            @if (!stats()?.payouts || stats()?.payouts?.length === 0) {
              <div class="text-center py-10 bg-stone-950/50 rounded-lg border border-stone-800">
                <p class="text-stone-500 text-xs">No historical payout transfers completed yet.</p>
              </div>
            } @else {
              <div class="overflow-x-auto">
                <table class="w-full text-left text-xs text-stone-300">
                  <thead class="bg-stone-950 text-stone-400 uppercase text-[10px] tracking-wider border-b border-stone-800">
                    <tr>
                      <th class="py-3 px-4">Date</th>
                      <th class="py-3 px-4">Method</th>
                      <th class="py-3 px-4">Transaction / Batch Ref</th>
                      <th class="py-3 px-4">Amount Paid</th>
                      <th class="py-3 px-4">Status</th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-stone-800 font-mono">
                    @for (payout of stats()?.payouts; track payout.id) {
                      <tr class="hover:bg-stone-800/40 transition-colors">
                        <td class="py-3.5 px-4 font-sans text-stone-300">
                          {{ payout.createdAt | date:'medium' }}
                        </td>
                        <td class="py-3.5 px-4 font-sans">
                          @if (payout.payoutMethod === 'paypal') {
                            <span class="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-sky-500/10 text-sky-400 border border-sky-500/30 text-[10px] font-bold uppercase">
                              PayPal API
                            </span>
                          } @else {
                            <span class="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-indigo-500/10 text-indigo-400 border border-indigo-500/30 text-[10px] font-bold uppercase">
                              Stripe Bank
                            </span>
                          }
                        </td>
                        <td class="py-3.5 px-4 text-stone-400">
                          {{ payout.paypalPayoutBatchId || payout.stripeTransferId }}
                        </td>
                        <td class="py-3.5 px-4 font-bold text-emerald-400">
                          {{ (payout.amountCents / 100) | currency:'USD':'symbol':'1.2-2' }}
                        </td>
                        <td class="py-3.5 px-4 font-sans">
                          <span class="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-400 border border-emerald-500/30 text-[11px] font-semibold">
                            Completed
                          </span>
                        </td>
                      </tr>
                    }
                  </tbody>
                </table>
              </div>
            }
          </section>
        }
      </div>
    </main>
  `,
})
export class AffiliatesDashboardComponent implements OnInit {
  protected readonly affiliateService = inject(AffiliateService);
  private readonly route = inject(ActivatedRoute);

  stats = this.affiliateService.dashboardStats;
  copied = signal(false);
  isConnectLoading = signal(false);
  isPortalLoading = signal(false);
  isSavingPaypal = signal(false);

  selectedPayoutTab = signal<'stripe' | 'paypal'>('stripe');
  paypalEmailInput = '';

  onboardingSuccess = signal<string | null>(null);
  onboardingWarning = signal<string | null>(null);

  ngOnInit(): void {
    const onboardingParam = this.route.snapshot.queryParams['onboarding'];
    if (onboardingParam === 'success') {
      this.onboardingSuccess.set('Bank account linked successfully! Direct deposit Net-30 payouts are active.');
    } else if (onboardingParam === 'refresh') {
      this.onboardingWarning.set('Stripe onboarding was refreshed or interrupted. Please complete bank account setup.');
    }

    const portalParam = this.route.snapshot.queryParams['portal'];
    if (portalParam === 'mock') {
      this.onboardingSuccess.set('Stripe Connect Portal opened in development mode.');
    }

    this.refreshData();
  }

  refreshData(): void {
    this.affiliateService.fetchDashboard().subscribe({
      next: (s) => {
        if (s?.user) {
          this.selectedPayoutTab.set(s.user.payoutMethod || 'stripe');
          this.paypalEmailInput = s.user.paypalEmail || s.user.email || '';
        }
      },
    });
  }

  setTab(method: 'stripe' | 'paypal'): void {
    this.selectedPayoutTab.set(method);
    if (method === 'paypal' && !this.paypalEmailInput) {
      this.paypalEmailInput = this.stats()?.user?.paypalEmail || this.stats()?.user?.email || '';
    }
    if (method === 'stripe' && this.stats()?.user?.payoutMethod === 'paypal') {
      // Switch back to Stripe method
      this.affiliateService.updatePayoutSettings('stripe').subscribe({
        next: () => this.onboardingSuccess.set('Payout method updated to Stripe Direct Deposit.'),
      });
    }
  }

  copyReferralLink(): void {
    const link = this.stats()?.referralLink;
    if (link) {
      navigator.clipboard.writeText(link);
      this.copied.set(true);
      setTimeout(() => this.copied.set(false), 2500);
    }
  }

  onSetupStripeConnect(): void {
    this.isConnectLoading.set(true);
    this.affiliateService.onboardStripeConnect().subscribe({
      next: (res) => {
        this.isConnectLoading.set(false);
        if (res.url) {
          window.location.href = res.url;
        }
      },
      error: (err) => {
        this.isConnectLoading.set(false);
        console.error('Stripe Connect onboarding error:', err);
      },
    });
  }

  onOpenStripePortal(): void {
    this.isPortalLoading.set(true);
    this.affiliateService.getStripeConnectPortalLink().subscribe({
      next: (res) => {
        this.isPortalLoading.set(false);
        if (res.url) {
          window.location.href = res.url;
        }
      },
      error: (err) => {
        this.isPortalLoading.set(false);
        console.error('Stripe Express Portal error:', err);
      },
    });
  }

  onSavePayPalSettings(): void {
    const cleanEmail = (this.paypalEmailInput || '').trim();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!cleanEmail || !emailRegex.test(cleanEmail)) {
      this.onboardingWarning.set('Please enter a valid PayPal email address (e.g. user@example.com).');
      this.onboardingSuccess.set(null);
      return;
    }

    this.isSavingPaypal.set(true);
    this.onboardingWarning.set(null);
    this.affiliateService.updatePayoutSettings('paypal', cleanEmail).subscribe({
      next: (res) => {
        this.isSavingPaypal.set(false);
        this.onboardingWarning.set(null);
        this.onboardingSuccess.set(res.message || 'PayPal API Payout email saved successfully!');
        if (res.stats?.user?.paypalEmail) {
          this.paypalEmailInput = res.stats.user.paypalEmail;
        }
      },
      error: (err) => {
        this.isSavingPaypal.set(false);
        this.onboardingSuccess.set(null);
        this.onboardingWarning.set(err.message || 'Failed to save PayPal settings.');
      },
    });
  }
}
